import type { CardDef } from '../types.js';
import type { TournamentDetail, LadderEntry, PrizeTier } from '../shared/tournament-types.js';
import type { Reward } from '../shared/rules.js';
import type { MenuAction } from './MenuView.js';
import { formatCountdown } from './Countdown.js';
import { ticketDisplayName } from '../shared/tickets.js';
import { showCardDetail } from './CardDetailView.js';
import type { PlayerData } from '../collection/PlayerState.js';
import {
  renderTournamentDeckPicker,
  setTournamentDeckId,
} from '../collection/TournamentDeckPrefs.js';

export type TournamentDetailAction =
  | MenuAction
  | { type: 'view-ladder'; tournamentId: string };

/** Detail view layout (matches a phone, tablet, and 1080p desktop):
 *
 *    ┌──────────────────────────────────────────┐
 *    │ [← Back]  Tournament Title  [Play Match] │  ← sticky header
 *    ├──────────────────────────────────────────┤
 *    │ description                              │
 *    │ countdown   |  rank line                 │
 *    │ blocking reason (when canPlay = false)   │
 *    │                                          │
 *    │ Ranking (top N, capped at 3 on this      │
 *    │   screen — see-full-ladder for more)     │
 *    │                                          │
 *    │ Prizes table                             │
 *    │ Per-match prizes table                   │
 *    └──────────────────────────────────────────┘
 *
 *  The header is pinned via `position: sticky` so the primary Play /
 *  Claim / Back affordances stay reachable while the player scrolls
 *  through prizes. Body content overflows naturally and the menu-screen
 *  modifier `.is-scrollable` switches the screen from centered-flex to
 *  flex-start + overflow-y:auto so the bottom of the page is reachable
 *  on short viewports.
 *
 *  Returns a teardown for the per-second countdown timer. */
export function createTournamentDetailView(
  container: HTMLElement,
  detail: TournamentDetail,
  catalog: Map<string, CardDef>,
  playerData: PlayerData,
  onAction: (action: TournamentDetailAction) => void,
): () => void {
  container.innerHTML = '';
  const el = document.createElement('div');
  el.className = 'menu-screen tournament-detail is-scrollable';

  const countdownText = detail.isEnded
    ? 'Ended'
    : formatCountdown(new Date(detail.endAt).getTime());

  const playerRow = detail.entered && detail.playerRank != null
    ? `<span class="tournament-detail-rank">Your rank: <strong>#${detail.playerRank}</strong> of ${detail.totalEntries}</span>`
    : `<span class="tournament-detail-rank">${detail.totalEntries} players</span>`;

  // Inline the deck picker with the rank line so the meta block reads
  // as one row. Hidden when the tournament isn't active (no match to
  // launch).
  const deckPickerHtml = detail.isActive
    ? renderTournamentDeckPicker(detail.defId, playerData)
    : '';

  el.innerHTML = `
    <div class="tournament-sticky-header">
      <button class="menu-btn-sm tournament-sticky-back" id="back-btn">&larr; Back</button>
      <h1 class="tournament-sticky-title">${escapeHtml(detail.displayName)}</h1>
      <div class="tournament-sticky-cta">${renderHeaderCta(detail)}</div>
    </div>
    <div class="tournament-detail-body">
      <p class="tournament-detail-desc">${escapeHtml(detail.description)}</p>
      <div class="tournament-detail-meta">
        <span class="tournament-detail-countdown">${countdownText}</span>
        <div class="tournament-detail-rank-row">
          ${playerRow}
          ${deckPickerHtml}
        </div>
      </div>
      ${renderBlockingNote(detail)}
      <section class="tournament-detail-section">
        <h2 class="section-title">Ranking</h2>
        <div class="tournament-ladder-preview">
          ${renderEntries(detail.topEntries, catalog)}
          ${detail.nearbyEntries
            ? `${renderTopNearbyDivider(detail.topEntries, detail.nearbyEntries)}${renderEntries(detail.nearbyEntries, catalog)}`
            : ''}
        </div>
        <button class="menu-btn-sm" id="view-ladder-btn">See full ladder</button>
      </section>
      <section class="tournament-detail-section">
        <h2 class="section-title">Prizes</h2>
        ${renderPrizes(detail.prizes, detail.perMatchPrizes, eligibleTierIndex(detail))}
      </section>
    </div>
  `;

  el.querySelector('#back-btn')!.addEventListener('click', () => onAction({ type: 'back-tournament-detail' }));
  el.querySelector('#view-ladder-btn')!.addEventListener('click', () => onAction({ type: 'view-ladder', tournamentId: detail.id }));
  el.querySelector('[data-action="play"]')?.addEventListener('click', () => onAction({ type: 'play-tournament', tournamentId: detail.id }));
  el.querySelector('[data-action="claim"]')?.addEventListener('click', () => onAction({ type: 'claim-tournament-prize', tournamentId: detail.id }));

  // Avatar taps open the standard card-detail modal. The avatar
  // cell carries the card id as a `data-card` attribute so we can
  // attach one delegated handler instead of N per-row listeners.
  el.querySelectorAll<HTMLElement>('.tournament-ladder-avatar[data-card]').forEach(node => {
    node.addEventListener('click', (e) => {
      const cardId = (e.currentTarget as HTMLElement).dataset.card;
      if (cardId) showCardDetail(cardId, catalog);
    });
  });

  // Deck preference dropdown — selection saves to localStorage,
  // keyed by tournament DEF id so the choice persists across
  // recurring cycles.
  const deckSelect = el.querySelector<HTMLSelectElement>('.tournament-deck-picker select');
  deckSelect?.addEventListener('change', () => {
    setTournamentDeckId(detail.defId, deckSelect.value);
  });

  container.appendChild(el);

  const tick = setInterval(() => {
    if (detail.isEnded) return;
    const span = el.querySelector('.tournament-detail-countdown');
    if (span) span.textContent = formatCountdown(new Date(detail.endAt).getTime());
  }, 1000);
  return () => clearInterval(tick);
}

/** Inline header CTA — kept terse so the sticky bar fits on narrow
 *  viewports. The full reason text (cooldown countdown, "no ticket",
 *  etc.) lives in `renderBlockingNote` below the header so a long
 *  message doesn't bust the sticky-bar layout. */
function renderHeaderCta(detail: TournamentDetail): string {
  if (detail.unclaimedPrize) {
    return `<button class="menu-btn primary-purple" data-action="claim">Claim Prize</button>`;
  }
  if (!detail.isActive) return '';
  if (!detail.canPlay) {
    return `<button class="menu-btn primary-purple" disabled>Play Match</button>`;
  }
  const label = detail.entered ? 'Play Match' : 'Join &amp; Play';
  return `<button class="menu-btn primary-purple" data-action="play">${label}</button>`;
}

function renderBlockingNote(detail: TournamentDetail): string {
  if (detail.canPlay || detail.isEnded || !detail.isActive) return '';
  if (!detail.blockingReason) return '';
  const text = detail.blockingReason === 'cooldown'
    ? `On cooldown — ${formatCountdown(Date.now() + detail.cooldownRemainingSeconds * 1000)} remaining`
    : detail.blockingReason === 'no-ticket'
      ? 'No ticket available for entry'
      : detail.blockingReason === 'tournament-ended'
        ? 'Tournament has ended'
        : 'Unable to play right now';
  return `<p class="tournament-detail-blocked">${escapeHtml(text)}</p>`;
}

/** Render the visual gap between the top slice and the "near me"
 *  slice — but ONLY if there's an actual gap. When the player's
 *  rank is close enough to the top that their nearby window starts
 *  one row below the top slice (e.g. top = [1,2,3], nearby starts
 *  at 4), the "…" is misleading because nothing is being elided.
 *  Empty / adjacent / overlapping ranges return an empty string so
 *  the two slices flow into one continuous list. */
function renderTopNearbyDivider(top: LadderEntry[], nearby: LadderEntry[]): string {
  if (top.length === 0 || nearby.length === 0) return '';
  const topEnd = top[top.length - 1].rank;
  const nearStart = nearby[0].rank;
  if (nearStart <= topEnd + 1) return '';
  return '<div class="tournament-ladder-gap">…</div>';
}

function renderEntries(entries: LadderEntry[], catalog: Map<string, CardDef>): string {
  if (entries.length === 0) return '<div class="tournament-ladder-empty">No entries.</div>';
  const rows = entries.map(e => renderEntryRow(e, catalog)).join('');
  return `<div class="tournament-ladder-rows">${rows}</div>`;
}

/** Shared row template for the summary ranking on the detail page.
 *  Columns: rank · avatar · name · record. The avatar carries a
 *  `data-card` attribute so the parent view's delegated click handler
 *  can open the card detail; for entries without a profile card (rare
 *  — every account that's chosen a starter has one), we render a
 *  monogram placeholder that isn't clickable. */
function renderEntryRow(e: LadderEntry, catalog: Map<string, CardDef>): string {
  return `
    <div class="tournament-ladder-row ${e.isMe ? 'is-me' : ''} ${e.isBot ? 'is-bot' : ''}">
      <span class="tournament-ladder-rank">#${e.rank}</span>
      ${renderAvatarCell(e, catalog)}
      <span class="tournament-ladder-name">${escapeHtml(e.displayName)}${e.isBot ? ' <span class="tournament-ladder-bot-tag">bot</span>' : ''}</span>
      <span class="tournament-ladder-record">${e.wins}-${e.losses}</span>
    </div>
  `;
}

function renderAvatarCell(e: LadderEntry, catalog: Map<string, CardDef>): string {
  const def = e.profileCardId ? catalog.get(e.profileCardId) : null;
  if (!def || !def.art) {
    const monogram = e.displayName.split(/\s+/).map(w => w[0] ?? '').join('').slice(0, 2).toUpperCase();
    return `<span class="tournament-ladder-avatar tournament-ladder-avatar-empty">${escapeHtml(monogram)}</span>`;
  }
  // `data-card` lets the view's delegated click handler open the
  // standard card-detail modal without re-binding per row. `loading=lazy`
  // lets the browser defer requests for off-screen rows; the ranking
  // section caps at ~13 rows so we don't need the ImageLoader queue
  // here, just native lazy.
  return `<span class="tournament-ladder-avatar" data-card="${escapeHtml(e.profileCardId!)}" role="button" tabindex="0" title="View card">
    <img src="${escapeHtml(def.art)}" alt="${escapeHtml(def.name)}" draggable="false" loading="lazy" />
  </span>`;
}

/** Index of the prize tier the player is eligible for given their
 *  current rank + total entries — mirrors server-side
 *  `resolveFinalPrize` so the highlight is consistent with what
 *  would actually be awarded if the tournament ended right now.
 *
 *  Precedence (highest specificity first):
 *    1. Exact-rank tier matching `playerRank` wins.
 *    2. Otherwise the smallest `topPercent` tier the player falls
 *       into. Smaller = more exclusive; "top 5%" beats "top 10%".
 *
 *  Returns `null` if no tier applies (player not entered, no
 *  matching tier, or invalid rank). */
function eligibleTierIndex(detail: TournamentDetail): number | null {
  if (!detail.entered || detail.playerRank == null) return null;
  const rank = detail.playerRank;
  const total = detail.totalEntries;
  if (rank < 1 || total < 1) return null;

  // 1. Exact rank match.
  for (let i = 0; i < detail.prizes.length; i++) {
    const t = detail.prizes[i];
    if ('rank' in t && t.rank === rank) return i;
  }

  // 2. Smallest topPercent the player falls into. Walk the prize
  // list in original order but pick the tier with the smallest
  // qualifying `topPercent`.
  const percentile = (rank / total) * 100;
  let bestIdx: number | null = null;
  let bestPercent = Infinity;
  for (let i = 0; i < detail.prizes.length; i++) {
    const t = detail.prizes[i];
    if (!('topPercent' in t)) continue;
    if (percentile <= t.topPercent && t.topPercent < bestPercent) {
      bestIdx = i;
      bestPercent = t.topPercent;
    }
  }
  return bestIdx;
}

function renderPrizes(
  tiers: PrizeTier[],
  perMatch: { win?: Reward; loss?: Reward },
  myTierIndex: number | null,
): string {
  const tierRows = tiers.map((tier, idx) => {
    // Special-case the "100%" bucket: in practice it means everyone
    // who participated, so "Everyone Else" reads more clearly than
    // "Top 100%" (which sounds like exclusivity when it's the
    // opposite).
    const label = 'rank' in tier
      ? `#${tier.rank}`
      : tier.topPercent >= 100
        ? 'Everyone Else'
        : `Top ${tier.topPercent}%`;
    const isMe = idx === myTierIndex;
    const cls = isMe ? ' class="is-my-tier"' : '';
    const youAreHere = isMe ? ' <span class="prize-you-are-here">You are here</span>' : '';
    return `<tr${cls}><td>${label}${youAreHere}</td><td>${rewardLabel(tier.reward)}</td></tr>`;
  }).join('');

  const perMatchRows = (perMatch.win || perMatch.loss)
    ? `<table class="tournament-prizes-table per-match">
         <thead><tr><th colspan="2">Per match</th></tr></thead>
         <tbody>
           ${perMatch.win ? `<tr><td>Win</td><td>${rewardLabel(perMatch.win)}</td></tr>` : ''}
           ${perMatch.loss ? `<tr><td>Loss</td><td>${rewardLabel(perMatch.loss)}</td></tr>` : ''}
         </tbody>
       </table>`
    : '';

  return `
    <table class="tournament-prizes-table">
      <thead><tr><th>Placement</th><th>Reward</th></tr></thead>
      <tbody>${tierRows}</tbody>
    </table>
    ${perMatchRows}
  `;
}

/** Render a Reward as one comma-separated HTML row, with each item
 *  color-coded by currency type so the prize table reads at a glance
 *  rather than as a wall of white text. Color treatment mirrors the
 *  per-currency classes already used by the game-over modal
 *  (`.coins-earned` gold, `.gems-earned` cyan, `.cards-earned` purple,
 *  `.tickets-earned` green) so the same hue signals the same value
 *  category everywhere in the app. */
function rewardLabel(reward: Reward): string {
  const parts: string[] = [];
  if (reward.coins) parts.push(`<span class="prize-coins">${reward.coins} coins</span>`);
  if (reward.gems) parts.push(`<span class="prize-gems">${reward.gems} gems</span>`);
  if (reward.cards && reward.cards.length > 0) {
    const label = reward.cards.length === 1 ? '1 card' : `${reward.cards.length} cards`;
    parts.push(`<span class="prize-cards">${label}</span>`);
  }
  if (reward.pack) {
    parts.push(`<span class="prize-cards">Pack (${reward.pack.cardCount})</span>`);
  }
  if (reward.tickets) {
    for (const t of reward.tickets) {
      parts.push(`<span class="prize-tickets">${t.count}× ${escapeHtml(ticketDisplayName(t.id))}</span>`);
    }
  }
  return parts.length === 0 ? '<span class="prize-empty">—</span>' : parts.join(', ');
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
